import { useState, type FormEvent } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/dah8buroh653ivfvno50';
const MAX_LENGTH = 500;

interface FormApiResponse {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
}

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [length, setLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      if (typeof value === 'string' && value.trim() !== '') {
        payload.append(key, value);
      }
    });

    setStatus('submitting');
    setErrorMsg('');

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormApiResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormApiResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK' && !isSpam;

      if (!isOk) {
        setErrorMsg(
          typeof serverMsg === 'string' && serverMsg.trim() !== ''
            ? serverMsg
            : '提交失败，请稍后重试或直接发送邮件给我们。',
        );
        setStatus('error');
        return;
      }

      setStatus('success');
      setLength(0);
      form.reset();
    } catch {
      setErrorMsg('网络异常，请检查网络连接后再试一次。');
      setStatus('error');
    }
  };

  return (
    <form
      data-readdy-form
      id="contact-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
      noValidate
    >
      <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">线索投稿 / 联系表单</h2>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        填好下面几项就能提交，信息越具体，编辑核实得越快。带 * 的项目为必填。
      </p>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label htmlFor="contact-name" className="text-sm text-foreground-800">
            称呼 *
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼你"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="contact-email" className="text-sm text-foreground-800">
            邮箱 *
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="用于接收回复"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="contact-type" className="text-sm text-foreground-800">
            线索类型 *
          </label>
          <select
            id="contact-type"
            name="type"
            required
            defaultValue="线索爆料"
            className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
          >
            <option value="线索爆料">线索爆料</option>
            <option value="内容纠错">内容纠错</option>
            <option value="商务合作">商务合作</option>
            <option value="其他反馈">其他反馈</option>
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="contact-city" className="text-sm text-foreground-800">
            发生城市
          </label>
          <input
            id="contact-city"
            name="city"
            type="text"
            placeholder="例如：成都（可不填）"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-2">
        <label htmlFor="contact-content" className="text-sm text-foreground-800">
          线索详情 *
        </label>
        <textarea
          id="contact-content"
          name="content"
          required
          rows={6}
          maxLength={MAX_LENGTH}
          onChange={(e) => setLength(e.target.value.length)}
          placeholder="请写清楚时间、地点和你能确认的细节，例如：9 月 10 日晚 21 点左右，某某音乐节现场……"
          className="w-full resize-y rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        />
        <span className="self-end text-xs text-foreground-500">
          {length} / {MAX_LENGTH} 字
        </span>
      </div>

      <div className="form-trap-field" aria-hidden="true">
        <label htmlFor="contact-website-alt">备用联系网址</label>
        <input
          id="contact-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      <div className="mt-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin text-base' : 'ri-send-plane-line text-sm'} />
          </span>
          {status === 'submitting' ? '提交中…' : '提交线索'}
        </button>
        <p className="text-xs leading-relaxed text-foreground-500">
          我们仅在核实线索时使用你填写的信息，不会对外公开邮箱地址。
        </p>
      </div>

      {status === 'success' && (
        <div className="mt-5 flex items-start gap-2.5 rounded-md border border-accent-300 bg-accent-50 px-4 py-3">
          <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-accent-700">
            <i className="ri-checkbox-circle-line text-base" />
          </span>
          <p className="text-sm leading-relaxed text-accent-900">
            已收到你的提交，编辑团队会在 1 个工作日内查看，信息完整的线索会优先处理。
          </p>
        </div>
      )}

      {status === 'error' && (
        <div className="mt-5 flex items-start gap-2.5 rounded-md border border-primary-300 bg-primary-50 px-4 py-3">
          <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-primary-700">
            <i className="ri-error-warning-line text-base" />
          </span>
          <p className="text-sm leading-relaxed text-primary-900">{errorMsg}</p>
        </div>
      )}
    </form>
  );
}